import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';

export default function DriftDetectionFeaturePage() {
  const faqs = [
    {
      question: 'What is drift detection?',
      answer: 'Drift detection tracks changes to branch protection rules and required checks, highlighting when control settings differ from your baseline.'
    },
    {
      question: 'How often are drift checks performed?',
      answer: 'Drift detection runs on a scheduled basis for enabled repositories. Frequency varies by plan tier.'
    },
    {
      question: 'What data is captured for drift events?',
      answer: 'EvidentTrail records before/after snapshots of branch protection settings, timestamps, and who made the change.'
    },
    {
      question: 'Can expected changes be acknowledged?',
      answer: 'Yes. Drift changes can be acknowledged with a reason to document intentional modifications.'
    }
  ];

  return (
    <MarketingLayout
      title="Drift Detection - Identify Control Gaps Before Audits"
      description="EvidentTrail drift detection identifies when branch protection rules change or are bypassed. Catch control gaps before auditors do."
      h1="Drift Detection"
      heroText="Monitor your GitHub branch protection rules continuously. Get alerted when controls change or are bypassed — before your next audit."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'Drift Detection', url: 'https://evidenttrail.com/features/drift-detection' }
        ]}
      />
      <FaqJsonLd items={faqs} />
      {/* What It Produces */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>What Drift Detection Monitors</h2>
            <div className={tokens.card}>
              <p className={`${tokens.text.base} mb-6`}>
                Drift detection continuously compares your repository settings against a baseline:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Required reviewers</strong> — Has the minimum reviewer count changed?
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Status checks</strong> — Are required CI checks still enforced?
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Admin bypass</strong> — Can admins push directly to protected branches?
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Stale review dismissal</strong> — Are reviews dismissed when new commits are pushed?
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Force push protection</strong> — Is force pushing blocked on main branches?
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">AI-assisted context</strong> — Includes context for AI-assisted changes when present
                  </span>
                </li>
              </ul>
            </div>
            <p className={`${tokens.text.base} mt-6`}>
              Need workflow-level coverage too? Review{' '}
              <Link to="/github-workflow-drift-detection" className="text-primary-400 hover:underline">GitHub workflow drift detection</Link>
              for monitoring required checks and workflow changes. For audit-ready branch protection timelines, see{' '}
              <Link to="/branch-protection-drift-detection" className="text-primary-400 hover:underline">branch protection drift detection</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* Why Auditors Care */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Why Auditors Care About Drift</h2>
            <div className="space-y-6">
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Controls Can Change</h3>
                <p className={tokens.text.base}>
                  Branch protection rules can be modified by repository admins at any time.
                  A setting that was enabled during your last audit may not be enabled today.
                </p>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Point-in-Time vs Continuous</h3>
                <p className={tokens.text.base}>
                  Auditors increasingly ask: "Were these controls in place <em>throughout</em> the audit period?"
                  Drift detection provides evidence that controls were consistently applied.
                </p>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Documented Exceptions</h3>
                <p className={tokens.text.base}>
                  When drift occurs, EvidentTrail records it as an exception. This creates a paper trail
                  showing you were aware of the change and can explain why it happened.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Drift Event */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Sample Drift Event</h2>
            <div className={`${tokens.card} font-mono text-sm`}>
              <pre className="text-slate-300 overflow-x-auto">
                {`{
  "eventType": "drift.detected",
  "timestamp": "2026-01-02T09:15:33Z",
  "repository": "acme-corp/api-service",
  "branch": "main",
  "changes": [
    {
      "setting": "required_approving_review_count",
      "previous": 2,
      "current": 1,
      "severity": "high"
    }
  ],
  "detectedBy": "system",
  "status": "open"
}`}
              </pre>
            </div>
            <p className={`${tokens.text.small} mt-4 text-slate-400`}>
              <Link to="/sample-drift-event" className="text-primary-400 hover:underline">
                View the full sample drift event →
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* What You Can Do */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Respond to Drift</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className={tokens.card}>
                <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center mb-4">
                  <span className="text-white font-semibold">1</span>
                </div>
                <h3 className={`${tokens.heading.h3} mb-2`}>Get Alerted</h3>
                <p className={tokens.text.base}>
                  Receive email or webhook notifications when settings change from your baseline.
                  Configure alert severity and recipients in settings.
                </p>
              </div>
              <div className={tokens.card}>
                <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center mb-4">
                  <span className="text-white font-semibold">2</span>
                </div>
                <h3 className={`${tokens.heading.h3} mb-2`}>Review Snapshots</h3>
                <p className={tokens.text.base}>
                  View snapshot history with side-by-side diffs of baseline vs current rules.
                  Filter by repository, branch, and date range on the snapshot history page.
                </p>
              </div>
              <div className={tokens.card}>
                <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center mb-4">
                  <span className="text-white font-semibold">3</span>
                </div>
                <h3 className={`${tokens.heading.h3} mb-2`}>Acknowledge Drift</h3>
                <p className={tokens.text.base}>
                  Document exceptions with a required reason and optional expiry.
                  Optionally attach to formal exception cases for compliance workflows.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqs.map((item) => (
                <div key={item.question} className={tokens.card}>
                  <h3 className={`${tokens.heading.h3} mb-2`}>{item.question}</h3>
                  <p className={tokens.text.base}>{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className={`${tokens.heading.h2} mb-4`}>Catch Drift Before Your Auditor Does</h2>
            <p className={`${tokens.text.base} mb-8`}>
              Start monitoring your branch protection settings today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={appLinks.register} className="btn btn-primary text-lg px-8 py-3">
                Get Started
              </a>
              <Link to="/features/exception-tracking" className="text-slate-300 hover:text-white transition-colors">
                Learn about Exception Tracking →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
